import { useEffect, useRef, useState } from "react";
import ComplexEditForm from "./ComplexEditForm";
import { createPortal } from "react-dom";
import { createAdminComplex, fetchAdminComplexById, patchAdminComplex } from "../../admin/api";

const modalStyles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(5, 10, 18, 0.76)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "var(--admin-modal-gutter)",
    zIndex: "var(--z-admin-modal)",
  },
  modal: {
    width: "min(var(--admin-modal-lg), calc(100vw - 2 * var(--admin-modal-gutter)))",
    maxHeight: "calc(100dvh - 2 * var(--admin-modal-gutter))",
    overflow: "hidden",
    backgroundColor: "var(--header-bg)",
    borderRadius: "var(--admin-modal-radius)",
    border: "1px solid var(--border)",
    boxShadow: "0 30px 90px rgba(0, 0, 0, 0.45)",
    padding: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--admin-gap-lg)",
    marginBottom: 0,
    padding: "var(--admin-modal-padding)",
  },
  title: {
    margin: 0,
    fontSize: "var(--admin-title-size)",
    fontWeight: 800,
    lineHeight: 1.15,
    color: "var(--text-main)",
  },
  closeButton: {
    width: "var(--admin-control-height)",
    height: "var(--admin-control-height)",
    borderRadius: "999px",
    border: "1px solid var(--border-strong)",
    backgroundColor: "var(--card-bg)",
    color: "var(--text-soft)",
    fontSize: "18px",
    lineHeight: 1,
    cursor: "pointer",
  },
  tabs: {
    display: "flex",
    gap: "var(--admin-gap-sm)",
    margin: "0 var(--admin-modal-padding) var(--admin-gap-lg)",
  },
  tabButton: {
    border: "1px solid var(--border)",
    backgroundColor: "var(--card-bg)",
    color: "var(--text-soft)",
    borderRadius: "var(--admin-radius)",
    padding: "var(--admin-button-padding-y) var(--admin-button-padding-x)",
    fontSize: "var(--admin-text-sm)",
    fontWeight: 700,
    cursor: "pointer",
  },
  tabButtonActive: {
    borderColor: "var(--accent-border)",
    backgroundColor: "var(--accent-soft)",
    color: "var(--text-main)",
  },
  jsonForm: {
    display: "flex",
    flexDirection: "column",
    gap: "var(--admin-gap-lg)",
  },
  jsonEditor: {
    width: "100%",
    minHeight: "min(360px, 56dvh)",
    padding: "var(--admin-control-padding-y) var(--admin-control-padding-x)",
    borderRadius: "var(--admin-radius)",
    border: "1px solid var(--border)",
    backgroundColor: "var(--field-bg)",
    color: "var(--text-main)",
    fontSize: "var(--admin-text-sm)",
    lineHeight: 1.5,
    fontFamily: "Consolas, Monaco, monospace",
    boxSizing: "border-box",
    resize: "vertical",
    outline: "none",
  },
  error: {
    padding: "var(--admin-control-padding-y) var(--admin-control-padding-x)",
    borderRadius: "var(--admin-radius)",
    backgroundColor: "var(--danger-soft)",
    border: "1px solid var(--danger)",
    color: "var(--danger)",
    fontSize: "var(--admin-text)",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "var(--admin-gap)",
  },
  secondaryButton: {
    border: "1px solid var(--border-strong)",
    backgroundColor: "var(--card-bg-soft)",
    color: "var(--text-soft)",
    borderRadius: "var(--admin-radius)",
    padding: "var(--admin-button-padding-y) var(--admin-button-padding-x)",
    fontSize: "var(--admin-text)",
    fontWeight: 600,
    cursor: "pointer",
  },
  primaryButton: {
    border: "none",
    backgroundColor: "var(--accent)",
    color: "var(--button-text)",
    borderRadius: "var(--admin-radius)",
    padding: "var(--admin-button-padding-y) var(--admin-button-padding-x)",
    fontSize: "var(--admin-text)",
    fontWeight: 600,
    cursor: "pointer",
  },
};

function EditComplexModal({ complex, mode = complex?._id ? "edit" : "create", isOpen, onClose, onSaved, focusEmbeddedSipClientId }) {
  const [activeTab, setActiveTab] = useState("form");
  const [jsonValue, setJsonValue] = useState("");
  const [jsonError, setJsonError] = useState("");
  const [isSavingJson, setIsSavingJson] = useState(false);
  const [displayedComplex, setDisplayedComplex] = useState(complex);
  const refreshRequestRef = useRef(0);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !complex) {
      return;
    }

    setDisplayedComplex(complex);
    setActiveTab("form");
    setJsonValue(JSON.stringify(complex, null, 2));
    setJsonError("");
    setIsSavingJson(false);
  }, [complex, isOpen]);

  if (!isOpen || !displayedComplex) {
    return null;
  }

  const handleFormSaved = async (savedComplex, options = {}) => {
    const requestId = ++refreshRequestRef.current;
    let actualComplex = savedComplex;
    const complexId = savedComplex?._id || displayedComplex?._id;
    if (complexId) {
      try {
        actualComplex = await fetchAdminComplexById(complexId);
      } catch {
        actualComplex = savedComplex;
      }
    }
    if (requestId !== refreshRequestRef.current) return;
    if (actualComplex && typeof actualComplex === "object") {
      setDisplayedComplex(actualComplex);
      setJsonValue(JSON.stringify(actualComplex, null, 2));
    }
    onSaved(actualComplex, options);
  };

  const handleJsonSubmit = async (event) => {
    event.preventDefault();
    setJsonError("");
    setIsSavingJson(true);

    try {
      const parsed = JSON.parse(jsonValue);

      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("JSON должен быть объектом");
      }

      const { _id, __v, createdAt, updatedAt, ...payload } = parsed;

      const responseData = displayedComplex._id
        ? await patchAdminComplex(displayedComplex._id, payload)
        : await createAdminComplex(payload);

      const savedComplex =
        responseData?.item || responseData?.complex || responseData;

      onSaved(savedComplex || payload);
      onClose();
    } catch (submitError) {
      setJsonError(
        submitError instanceof Error
          ? submitError.message
          : "Не удалось сохранить JSON"
      );
    } finally {
      setIsSavingJson(false);
    }
  };

  return createPortal(
    <div className="edit-complex-modal__overlay" style={modalStyles.overlay}>
      <div
        className="edit-complex-modal"
        style={modalStyles.modal}
      >
        <div className="edit-complex-modal__topbar" style={modalStyles.topBar}>
          <h2 style={modalStyles.title}>
            {displayedComplex._id ? displayedComplex.name || "Редактирование ЖК" : "Добавить ЖК"}
          </h2>
          <button
            aria-label="Закрыть модальное окно"
            className="edit-complex-modal__close"
            onClick={onClose}
            style={modalStyles.closeButton}
            type="button"
          >
            ×
          </button>
        </div>

        <div className="edit-complex-modal__tabs" style={modalStyles.tabs}>
          <button
            className={`edit-complex-modal__tab${
              activeTab === "form" ? " edit-complex-modal__tab--active" : ""
            }`}
            type="button"
            style={{
              ...modalStyles.tabButton,
              ...(activeTab === "form" ? modalStyles.tabButtonActive : {}),
            }}
            onClick={() => setActiveTab("form")}
          >
            Форма
          </button>

          <button
            className={`edit-complex-modal__tab${
              activeTab === "json" ? " edit-complex-modal__tab--active" : ""
            }`}
            type="button"
            style={{
              ...modalStyles.tabButton,
              ...(activeTab === "json" ? modalStyles.tabButtonActive : {}),
            }}
            onClick={() => setActiveTab("json")}
          >
            JSON
          </button>
        </div>

        {activeTab === "form" ? (
          <ComplexEditForm complex={displayedComplex} mode={mode} focusEmbeddedSipClientId={focusEmbeddedSipClientId} onClose={onClose} onSaved={handleFormSaved} />
        ) : (
          <form className="edit-complex-modal__json-form" onSubmit={handleJsonSubmit} style={modalStyles.jsonForm}>
            {jsonError ? <div style={modalStyles.error}>{jsonError}</div> : null}

            <textarea
              className="edit-complex-modal__json"
              disabled={isSavingJson}
              onChange={(event) => setJsonValue(event.target.value)}
              spellCheck="false"
              style={modalStyles.jsonEditor}
              value={jsonValue}
            />

            <div className="edit-complex-modal__actions" style={modalStyles.actions}>
              <button
                className="edit-complex-modal__button"
                disabled={isSavingJson}
                onClick={onClose}
                style={{
                  ...modalStyles.secondaryButton,
                  opacity: isSavingJson ? 0.7 : 1,
                  cursor: isSavingJson ? "not-allowed" : "pointer",
                }}
                type="button"
              >
                Отмена
              </button>

              <button
                className="edit-complex-modal__button edit-complex-modal__button--primary"
                disabled={isSavingJson}
                style={{
                  ...modalStyles.primaryButton,
                  opacity: isSavingJson ? 0.7 : 1,
                  cursor: isSavingJson ? "not-allowed" : "pointer",
                }}
                type="submit"
              >
                {displayedComplex._id ? "Сохранить JSON" : "Добавить JSON"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}

export default EditComplexModal;
